import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormLabel, Grid, MenuItem, Switch, TextField, Tooltip, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {
    ID_ACCESSORIES,
    ID_BE_FREE_EVO, ID_CAR_BOX, ID_CAR_CLOUD,
    ID_COMMISSIONS,
    ID_DAMAGE, ID_FLEX_RENT,
    ID_FUEL, ID_VOUCHERS
} from "../../mobilitystore/constants/PrintoutTypes";
import { ID_CHECKIN, ID_CHECKOUT } from "../constants/AppliedOn";
import { ID_STANDARD } from "../constants/ClusterStatuses";
import * as Yup from 'yup';
import { HighlightOffRounded, InfoRounded } from "@mui/icons-material";
import { ID_EXCLUDELASTRENTAL } from "../constants/VoiceSpecial";
import { useClusterVoicesRepository } from "../../../api/repositories/ClusterVoicesRepository";
import { STATE_VOICES_TABLE_UPDATING } from '../constants/ClustersRecoil'
import { useSetRecoilState } from "recoil";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormSelect from "../../../experiments/forms/FormSelect";
import FormAsyncAutocomplete from "../../../experiments/forms/FormAsyncAutocomplete";
import FormTextField from "../../../experiments/forms/FormTextField";
import {
    CORPORATE,
    REPLACEMENT,
    RETAIL,
    SUBSCRIPTION,
    TOUR_OPERATOR,
    UNDEFINED
} from "../../mobilitystore/constants/Segments";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

interface ModalProps {
    open: boolean,
    onClose: Function,
    type: string,
    voice?: any,
    clusterId: number,
    legalEntity: string,
    dealer: string,
    clusterType: string
}

interface VoiceInput {
    appliedOn: string,
    printoutType: string,
    special: string,
    order: number,
    dd1To7: string,
    dd8To14: string,
    dd15To21: string,
    dd22To31: string,
    voice: any,
    fuel: boolean,
    segment: string
}

const VoiceModal = (props: ModalProps) => {
    const { t } = useTranslation('finance', { keyPrefix: 'clusters' });
    const { getUnusedVoiceItems, createVoice, updateVoice, getAvailableSegments } = useClusterVoicesRepository();
    const setUpdating = useSetRecoilState(STATE_VOICES_TABLE_UPDATING);

    const [disableSegment, setDisableSegment] = useState(true);
    const { dispatchGlobalData } = useContext(GlobalContext);

    const schema = Yup.object().shape({
        appliedOn: Yup.string().required(),
        printoutType: Yup.string().required(),
        special: Yup.string(),
        order: Yup.number().required(),
        dd1To7: Yup.string().required().test('dd1To7', t('voice-modal.form.dd-error'), (value) => {
            return value !== null && value !== undefined && value.match(/^\s*-?\d*(\.\d{0,4})?\s*$/gm) !== null;
        }),
        dd8To14: Yup.string().required().test('dd8To14', t('voice-modal.form.dd-error'), (value) => {
            return value !== null && value !== undefined && value.match(/^\s*-?\d*(\.\d{0,4})?\s*$/gm) !== null;
        }),
        dd15To21: Yup.string().required().test('dd15To21', t('voice-modal.form.dd-error'), (value) => {
            return value !== null && value !== undefined && value.match(/^\s*-?\d*(\.\d{0,4})?\s*$/gm) !== null;
        }),
        dd22To31: Yup.string().required().test('dd22To31', t('voice-modal.form.dd-error'), (value) => {
            return value !== null && value !== undefined && value.match(/^\s*-?\d*(\.\d{0,4})?\s*$/gm) !== null;
        }),
        voice: Yup.object().required(),
        fuel: Yup.boolean(),
        segment: Yup.string()
    });

    const defaultValues = {
        appliedOn: '',
        printoutType: '',
        special: '',
        order: undefined,
        dd1To7: '',
        dd8To14: '',
        dd15To21: '',
        dd22To31: '',
        voice: '',
        fuel: false,
        segment: ''
    };

    const { control, reset, watch, setValue, clearErrors, handleSubmit, formState } = useForm<VoiceInput>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    const watcher = watch();

    const onSubmit: SubmitHandler<VoiceInput> = data => {
        if (props.type === 'add') {
            createVoice(props.clusterId, {
                appliedOn: data.appliedOn,
                printoutType: data.printoutType,
                special: data.special,
                ord: data.order,
                voice: data.voice.key,
                dd1_7: parseFloat(data.dd1To7),
                dd8_14: parseFloat(data.dd8To14),
                dd15_21: parseFloat(data.dd15To21),
                dd22_31: parseFloat(data.dd22To31),
                fuelOnly: data.fuel,
                segment: data?.segment ? data.segment : null,
                legalEntity: props.legalEntity,
                dealer: props.dealer
            }).then((res) => {
                if(res.status === 'KO'){
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('error-dialog.errors.'+res.message),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            },
                        },
                    });
                } else {
                    props.onClose();
                    cleanForm();
                    setUpdating(true)
                }
            });
        } else {
            if (props.voice !== undefined && props.voice.id !== undefined) {
                 updateVoice(props.clusterId,props.voice.id, {
                                    appliedOn: data.appliedOn,
                                    printoutType: data.printoutType,
                                    special: data.special,
                                    ord: data.order,
                                    voice: data.voice.key,
                                    dd1_7: parseFloat(data.dd1To7),
                                    dd8_14: parseFloat(data.dd8To14),
                                    dd15_21: parseFloat(data.dd15To21),
                                    dd22_31: parseFloat(data.dd22To31),
                                    fuelOnly: data.fuel,
                                    segment: data?.segment ? data.segment : null
                 }).then((res) => {
                                    if(res.status === 'KO'){
                                        dispatchGlobalData({
                                            type: setSnackbar,
                                            payload: {
                                                snackbar: {
                                                    message: t('error-dialog.errors.'+res.message),
                                                    type: snackbarTypes.error,
                                                    show: true,
                                                    autoClose: true,
                                                },
                                            },
                                        });
                                    } else {
                                        props.onClose();
                                        cleanForm();
                                        setUpdating(true)
                                    }
                                }); 
            }
        }
    }

    useEffect(() => {
        if (props.voice && props.type === 'edit' && props.open) {
            setValue('appliedOn', props.voice.appliedOn);
            setValue('printoutType', props.voice.printoutType);
            setValue('special', props.voice.special || '');
            setValue('order', parseInt(props.voice.ord));
            setValue('voice', { key: props.voice.voice, value: props.voice.voiceName });
            setValue('dd1To7', props.voice.dd1To7);
            setValue('dd8To14', props.voice.dd8To14);
            setValue('dd15To21', props.voice.dd15To21);
            setValue('dd22To31', props.voice.dd22To31);
            setValue('fuel', props.voice.fuelOnly);
            setValue('segment', props.voice.segment);

            setDisableSegment(props.voice.segment === undefined || props.voice.segment === UNDEFINED);
        }

        if (props.voice && props.type === 'add' && props.open) {
            setValue('order', parseInt(""));
            setDisableSegment(true)
        }

    }, [props.open])


    useEffect(() => {
        setDisableSegment(!watcher.voice.configurable);
        if(!watcher.voice.configurable){
            setValue('segment', '');
        }

    }, [watcher.voice.configurable])

    const cleanForm = () => {
        reset(defaultValues);
        clearErrors();
    }

    const printoutTypes = [
        {
            label: t("printout-types." + ID_ACCESSORIES),
            value: ID_ACCESSORIES,
            selected: true
        },
        {
            label: t("printout-types." + ID_FUEL),
            value: ID_FUEL
        },
        {
            label: t("printout-types." + ID_COMMISSIONS),
            value: ID_COMMISSIONS
        },
        {
            label: t("printout-types." + ID_DAMAGE),
            value: ID_DAMAGE
        },
        {
            label: t("printout-types." + ID_VOUCHERS),
            value: ID_VOUCHERS
        },
        {
            label: t("printout-types." + ID_BE_FREE_EVO),
            value: ID_BE_FREE_EVO
        },
        {
            label: t("printout-types." + ID_CAR_CLOUD),
            value: ID_CAR_CLOUD
        },
        {
            label: t("printout-types." + ID_FLEX_RENT),
            value: ID_FLEX_RENT
        },
        {
            label: t("printout-types." + ID_CAR_BOX),
            value: ID_CAR_BOX
        }
    ].filter(option => props.clusterType === ID_STANDARD || option.value === ID_COMMISSIONS);

    const appliedOn = [
        {
            label: t("applied-on." + ID_CHECKIN),
            value: ID_CHECKIN,
            selected: true
        },
        {
            label: t("applied-on." + ID_CHECKOUT),
            value: ID_CHECKOUT
        },
    ];

    const special = [
        {
            label: t("specials.NONE"),
            value: ''
        },
        {
            label: t("specials." + ID_EXCLUDELASTRENTAL),
            value: ID_EXCLUDELASTRENTAL
        }
    ];

    const segments = [
        {
            label: t("segments." + TOUR_OPERATOR),
            value: TOUR_OPERATOR,
            selected: true
        },
        {
            label: t("segments." + REPLACEMENT),
            value: REPLACEMENT
        },
        {
            label: t("segments." + CORPORATE),
            value: CORPORATE
        },
        {
            label: t("segments." + RETAIL),
            value: RETAIL
        },
        {
            label: t("segments." + SUBSCRIPTION),
            value: SUBSCRIPTION
        }
    ];



    return (
        <Dialog maxWidth={"lg"} disableEscapeKeyDown fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {props.type === 'edit' ? t('voice-modal.title.edit') + " " + props.voice?.id : t('voice-modal.title.add')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        props.onClose();
                        cleanForm();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <HighlightOffRounded />
                </IconButton>
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit, (errors, event) => console.log(errors))}>
                <DialogContent>
                    <Grid container spacing={2} sx={{ padding: "15px" }} alignItems={"center"}>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name="appliedOn"
                                control={control}
                                props={{
                                    style: { width: "100%" },
                                    label: t('voice-modal.form.applied-on')
                                }}
                                items={appliedOn.map((option: any,) => (
                                    <MenuItem key={option.value}
                                        value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                control={control}
                                name="printoutType"
                                props={{
                                    onChange: (e: any) => {
                                        setValue('voice', '', { shouldTouch: true })
                                        setValue('segment', '', { shouldTouch: true })
                                    },
                                    style: { width: "100%" },
                                    label: t('voice-modal.form.printout-type')
                                }}
                                items={printoutTypes.map((option: any) => (
                                    <MenuItem key={option.value}
                                        value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormAsyncAutocomplete
                                name={'voice'}
                                control={control}
                                props={{
                                    disabled: watcher.printoutType === '',
                                    initialValues: true,
                                    callback: (it) => getUnusedVoiceItems(watcher.printoutType, it, props.clusterId.toString(), props.legalEntity, props.dealer)
                                        .then(it => {
                                            setDisableSegment(!it.result[0]?.configurable)
                                            return it.result
                                        }),
                                    renderInput: (params) => <TextField
                                        {...params}
                                        label={t('voice-modal.form.voice')}
                                    />
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={'segment'}
                                control={control}
                                props={{
                                    defaultValue: "",
                                    disabled: disableSegment,
                                    style: { width: "100%" },
                                    label: t('voice-modal.form.segment')
                                }}
                                items={segments.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'dd1To7'}
                                matcher={/^\s*-?\d*(\.\d{0,4})?\s*$/gm}
                                props={{
                                    onBlur: () => {
                                        if (!formState.dirtyFields.dd8To14 && !formState.dirtyFields.dd15To21 && !formState.dirtyFields.dd22To31 && props.type === 'add') {
                                            setValue('dd8To14', watcher.dd1To7, { shouldDirty: true, shouldTouch: true });
                                            setValue('dd15To21', watcher.dd1To7, { shouldDirty: true, shouldTouch: true });
                                            setValue('dd22To31', watcher.dd1To7, { shouldDirty: true, shouldTouch: true });
                                        }
                                    },
                                    label: t('voice-modal.form.dd1_7'),
                                    sx: { width: '100%' },
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'dd8To14'}
                                matcher={/^\s*-?\d*(\.\d{0,4})?\s*$/gm}
                                props={{

                                    label: t('voice-modal.form.dd8_14'),
                                    sx: { width: '100%' },
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'dd15To21'}
                                matcher={/^\s*-?\d*(\.\d{0,4})?\s*$/gm}
                                props={{
                                    label: t('voice-modal.form.dd15_21'),
                                    sx: { width: '100%' },
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'dd22To31'}
                                matcher={/^\s*-?\d*(\.\d{0,4})?\s*$/gm}
                                props={{
                                    label: t('voice-modal.form.dd22_31'),
                                    sx: { width: '100%' },
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name="special"
                                control={control}
                                props={{
                                    style: { width: "100%" },
                                    label: t('voice-modal.form.special')
                                }}
                                items={special.map((option: any) => (
                                    <MenuItem key={option.value}
                                        value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'order'}
                                props={{
                                    sx: { width: '100%' },
                                    label: t('voice-modal.form.order'),
                                    type: 'number'
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Controller
                                    name={'fuel'}
                                    control={control}
                                    render={({ field: { value, onChange } }) => (<Switch checked={value} onChange={onChange} disabled={watcher.printoutType !== ID_FUEL} />)} />

                                <FormLabel sx={{ paddingLeft: '5px' }}>{t('voice-modal.form.fuel-only')}</FormLabel>
                                <Tooltip title={t('voice-modal.form.fuel-tooltip')}>
                                    <InfoRounded sx={{ paddingLeft: '5px', color: 'accent.main' }} />
                                </Tooltip>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ padding: "10px" }}>
                    <Button variant={"outlined"} color={"accent"}
                        onClick={() => {
                            props.onClose();
                            cleanForm();
                        }}>{t('voice-modal.buttons.reset')}</Button>
                    <Button color={"accent"} type="submit">{t('voice-modal.buttons.apply')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
export default VoiceModal;